import { type ChartConfig, ChartContainer } from "@d12labs/ui/chart";
import { useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, ReferenceLine, Tooltip, XAxis, YAxis } from "recharts";
import { fmtUsd, harnessLabel } from "../aggregate";
import { type DailyPoint, harnessColor, type Overview } from "../overview";
import { dayLabel, HarnessDot } from "./spend-summary";

/**
 * The chart's stacked series: every real harness with spend in the daily data,
 * highest total first (bottom of the stack up). Derived from `byHarness`, not
 * the hero rows: the hero re-slices Claude Code into a `claude-other` pseudo
 * row and drops the `claude` row when every Claude session ran on other
 * models, while the daily data stays keyed by real harness. Reading the hero
 * would then lose the `claude` series (and could add a phantom one).
 */
export function dailyHarnesses(points: DailyPoint[]): string[] {
	const totals = new Map<string, number>();
	for (const point of points) {
		for (const [harness, cost] of Object.entries(point.byHarness)) {
			totals.set(harness, (totals.get(harness) ?? 0) + cost);
		}
	}
	return [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([harness]) => harness);
}

/** A "nice" axis max (1, 2, 2.5, 5 × 10ⁿ) and four evenly spaced ticks up to it. */
function yTicks(max: number): number[] {
	if (max <= 0) return [0, 1];
	const raw = max / 3;
	const mag = 10 ** Math.floor(Math.log10(raw));
	const step = ([1, 2, 2.5, 5, 10].find((m) => m * mag >= raw) ?? 10) * mag;
	return [0, step, step * 2, step * 3].concat(step * 3 < max ? [step * 4] : []);
}

function axisUsd(value: number): string {
	if (value >= 1000) return `$${(value / 1000).toFixed(value % 1000 ? 1 : 0)}k`;
	return `$${Number.isInteger(value) ? value : value.toFixed(2)}`;
}

/** Month starts for long spans; evenly spaced days for short ones. */
function xTicks(points: DailyPoint[]): { ticks: string[]; monthly: boolean } {
	if (points.length > 45) {
		return { ticks: points.filter((p) => p.day.endsWith("-01")).map((p) => p.day), monthly: true };
	}
	const every = Math.max(1, Math.ceil(points.length / 7));
	return { ticks: points.filter((_, i) => i % every === 0).map((p) => p.day), monthly: false };
}

function monthLabel(day: string): string {
	return new Date(`${day}T00:00:00Z`).toLocaleDateString("en-US", {
		month: "short",
		timeZone: "UTC",
	});
}

export function DailyChart({ overview }: { overview: Overview }) {
	const points = overview.daily;
	const [hover, setHover] = useState<number | null>(null);

	const series = useMemo(() => dailyHarnesses(points), [points]);
	const config = useMemo(
		() =>
			Object.fromEntries(
				series.map((h) => [h, { label: harnessLabel(h), color: harnessColor(h) }]),
			) satisfies ChartConfig,
		[series],
	);
	const data = useMemo(
		() => points.map((p) => ({ day: p.day, ...p.byHarness, sessions: p.sessions, total: p.total })),
		[points],
	);

	const peakIndex = points.reduce(
		(best, p, i) => (p.total > (points[best]?.total ?? 0) ? i : best),
		0,
	);
	const peak = points[peakIndex];
	const shownIndex = hover ?? (peak && peak.total > 0 ? peakIndex : null);
	const shown = shownIndex == null ? undefined : points[shownIndex];
	const ticks = yTicks(peak?.total ?? 0);
	const x = xTicks(points);

	return (
		<section
			aria-label="Cost per day"
			className="flex min-w-0 flex-col rounded-lg border border-hairline bg-surface-1"
		>
			<div className="flex flex-wrap items-start justify-between gap-3 border-b border-hairline px-4 py-2.5">
				<div>
					<h2 className="card-title text-foreground">Cost per day</h2>
					<p className="text-meta text-ink-subtle">
						Multi-day sessions split onto the UTC day each turn completed
					</p>
				</div>
				<ul className="flex flex-wrap items-center gap-3 text-meta text-ink-subtle">
					{series.map((h) => (
						<li key={h} className="inline-flex items-center gap-1.5">
							<HarnessDot harness={h} />
							{harnessLabel(h)}
						</li>
					))}
				</ul>
			</div>
			<div className="relative min-h-[212px] flex-1 px-2 pb-2 pt-3">
				{points.length === 0 ? (
					<p className="flex h-[200px] items-center justify-center text-body-sm text-ink-subtle">
						No spend in this view.
					</p>
				) : (
					<ChartContainer
						config={config}
						initialDimension={{ width: 800, height: 200 }}
						className="aspect-auto h-full min-h-[200px] w-full"
					>
						<BarChart
							data={data}
							barCategoryGap={points.length > 120 ? 0 : "15%"}
							margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
							onMouseMove={(state) => {
								const index = Number(state?.activeTooltipIndex);
								setHover(Number.isFinite(index) ? index : null);
							}}
							onMouseLeave={() => setHover(null)}
						>
							<CartesianGrid vertical={false} stroke="var(--color-hairline)" />
							<YAxis
								ticks={ticks}
								domain={[0, ticks.at(-1) ?? 1]}
								tickFormatter={axisUsd}
								tickLine={false}
								axisLine={false}
								width={48}
								tick={{ fontFamily: "var(--font-mono)", fontSize: 10 }}
							/>
							<XAxis
								dataKey="day"
								ticks={x.ticks}
								tickFormatter={x.monthly ? monthLabel : dayLabel}
								tickLine={false}
								axisLine={false}
								tick={{ fontSize: 10 }}
								interval={0}
							/>
							{/* Drives hover; the callout below renders the content. */}
							<Tooltip content={() => null} cursor={{ fill: "var(--color-surface-3)" }} />
							{series.map((h) => (
								<Bar
									key={h}
									dataKey={h}
									stackId="spend"
									fill={harnessColor(h)}
									isAnimationActive={false}
								/>
							))}
							{shown ? (
								<ReferenceLine
									x={shown.day}
									stroke="var(--color-ink)"
									strokeOpacity={0.3}
									strokeDasharray="2 3"
								/>
							) : null}
						</BarChart>
					</ChartContainer>
				)}
				{shown ? (
					<div
						aria-live="polite"
						className="pointer-events-none absolute right-4 top-3 rounded-md border border-hairline-strong bg-popover px-2.5 py-1.5 shadow-lg shadow-black/30"
					>
						<div className="text-[10px] uppercase tracking-[0.06em] text-ink-tertiary">
							{hover == null ? "Peak · " : ""}
							{dayLabel(shown.day)}
						</div>
						<div className="text-body-sm font-semibold tabular-nums text-foreground">
							{fmtUsd(shown.total)}{" "}
							<span className="font-normal text-ink-subtle">
								· {shown.sessions} {shown.sessions === 1 ? "session" : "sessions"}
							</span>
						</div>
					</div>
				) : null}
			</div>
		</section>
	);
}

export function TopRepos({
	overview,
	selected,
	onPick,
	onShowAll,
}: {
	overview: Overview;
	selected: string[];
	onPick: (repo: string) => void;
	onShowAll: () => void;
}) {
	const rows = overview.repos.slice(0, 7);
	const max = rows[0]?.spend ?? 0;
	return (
		<section
			aria-label="Top repos"
			className="min-w-0 rounded-lg border border-hairline bg-surface-1"
		>
			<div className="flex items-center justify-between border-b border-hairline px-4 py-2.5">
				<h2 className="card-title text-foreground">Top repos</h2>
				<button
					type="button"
					onClick={onShowAll}
					className="text-meta text-ink-subtle hover:text-foreground"
				>
					All {overview.repoOptions.length} →
				</button>
			</div>
			<div className="px-4 py-2">
				{rows.length === 0 ? (
					<p className="py-2 text-body-sm text-ink-subtle">No repos in this view.</p>
				) : (
					rows.map((row) => (
						<button
							key={row.repo}
							type="button"
							aria-pressed={selected.includes(row.repo)}
							onClick={() => onPick(row.repo)}
							title={`Filter to ${row.repo}`}
							className="group block w-full py-1.5 text-left"
						>
							<div className="flex items-baseline justify-between gap-2 text-body-sm">
								<span className="truncate text-ink-muted group-hover:text-foreground">
									{row.repo}
								</span>
								<span className="font-mono text-[11px] tabular-nums text-ink-subtle">
									{fmtUsd(row.spend)}
								</span>
							</div>
							<div className="mt-1 h-1 rounded-full bg-surface-3">
								<div
									className="h-full rounded-full bg-primary opacity-85"
									style={{ width: `${max ? (row.spend / max) * 100 : 0}%` }}
								/>
							</div>
						</button>
					))
				)}
			</div>
		</section>
	);
}
